/**
 * Apex API client — all form traffic goes through the Cloudflare Worker.
 * Never embed GITHUB_KEY or allowlists in the theme.
 */
(function () {
  'use strict';

  var STORAGE = {
    ADMIN_TOKEN: 'apex_admin_api_token',
    ADMIN_TOKEN_EXP: 'apex_admin_api_token_exp'
  };

  var FORM_KEYS = {
    contact: 'contact',
    application: 'dealer-application',
    'dealer-application': 'dealer-application',
    'find-installer': 'find-installer',
    newsletter: 'newsletter',
    'ppf-plan': 'ppf-plan'
  };

  function normalizeFormKey(form) {
    return FORM_KEYS[form] || form;
  }

  function getConfig() {
    var api = (window.theme && window.theme.apexApi) || {};
    return {
      baseUrl: (api.baseUrl || 'https://apex-api.nic-58f.workers.dev').replace(/\/$/, ''),
      token: api.workerToken || 'apex-theme-access'
    };
  }

  function apiHeaders(extra) {
    var cfg = getConfig();
    var headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-worker-token': cfg.token
    };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        headers[k] = extra[k];
      });
    }
    return headers;
  }

  /** Worker accepts x-worker-token header OR ?t= query param (Shopify may strip custom headers on cross-origin POST). */
  function apiUrl(path) {
    var cfg = getConfig();
    var url = cfg.baseUrl + path;
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + 't=' + encodeURIComponent(cfg.token);
  }

  function apiFetch(path, options) {
    var url = apiUrl(path);
    options = options || {};
    options.headers = apiHeaders(options.headers || {});
    return window.fetch(url, options).then(function (res) {
      return res.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!res.ok) {
          var message = (data && (data.error || data.message)) || 'Request failed';
          if (res.status === 401 && data && data.code === 'WORKER_TOKEN') {
            message =
              'Could not authenticate with the Apex API (worker token rejected). Check Theme settings → Apex API → Worker access token matches WORKER_TOKEN on the Cloudflare Worker, then try again.';
          } else if (res.status === 401 && data && data.error === 'Unauthorized' && path !== '/api/auth') {
            message = 'Session expired. Sign in again.';
          }
          var err = new Error(message);
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  var _memoryAdminToken = null;
  var _memoryAdminTokenExp = 0;

  function getStorageSafe(kind) {
    try {
      return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  function saveAdminToken(token, expiresIn, remember) {
    var exp = Date.now() + (expiresIn || 86400) * 1000;
    _memoryAdminToken = token;
    _memoryAdminTokenExp = exp;
    try {
      var sessionStore = getStorageSafe('session');
      var localStore = getStorageSafe('local');
      if (sessionStore) {
        sessionStore.setItem(STORAGE.ADMIN_TOKEN, token);
        sessionStore.setItem(STORAGE.ADMIN_TOKEN_EXP, String(exp));
      }
      if (remember && localStore) {
        localStore.setItem(STORAGE.ADMIN_TOKEN, token);
        localStore.setItem(STORAGE.ADMIN_TOKEN_EXP, String(exp));
      } else if (localStore) {
        localStore.removeItem(STORAGE.ADMIN_TOKEN);
        localStore.removeItem(STORAGE.ADMIN_TOKEN_EXP);
      }
    } catch (e) { /* ignore — memory fallback still works */ }
  }

  function readAdminToken() {
    if (_memoryAdminToken && Date.now() <= _memoryAdminTokenExp) {
      return _memoryAdminToken;
    }
    try {
      var stores = [getStorageSafe('session'), getStorageSafe('local')];
      for (var i = 0; i < stores.length; i++) {
        var store = stores[i];
        if (!store) continue;
        var token = store.getItem(STORAGE.ADMIN_TOKEN);
        var exp = parseInt(store.getItem(STORAGE.ADMIN_TOKEN_EXP) || '0', 10);
        if (token && exp && Date.now() <= exp) {
          _memoryAdminToken = token;
          _memoryAdminTokenExp = exp;
          return token;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function clearAdminToken() {
    _memoryAdminToken = null;
    _memoryAdminTokenExp = 0;
    try {
      var sessionStore = getStorageSafe('session');
      var localStore = getStorageSafe('local');
      if (sessionStore) {
        sessionStore.removeItem(STORAGE.ADMIN_TOKEN);
        sessionStore.removeItem(STORAGE.ADMIN_TOKEN_EXP);
      }
      if (localStore) {
        localStore.removeItem(STORAGE.ADMIN_TOKEN);
        localStore.removeItem(STORAGE.ADMIN_TOKEN_EXP);
      }
    } catch (e) { /* ignore */ }
  }

  function adminAuthHeaders() {
    var headers = {};
    var token = readAdminToken();
    if (token) headers.Authorization = 'Bearer ' + token;
    return headers;
  }

  function collectContactFormData(form) {
    var data = {};
    var fd = new FormData(form);
    fd.forEach(function (value, key) {
      var v = (value || '').toString().trim();
      if (!v) return;
      if (key.indexOf('contact[') === 0) {
        data[key.slice(8, -1)] = v;
      } else {
        data[key] = v;
      }
    });
    if (data.email) data.email = data.email.toLowerCase();
    return data;
  }

  function bindApexForm(form) {
    if (!form || form.getAttribute('data-apex-bound') === '1') return;
    form.setAttribute('data-apex-bound', '1');
    form.setAttribute('novalidate', 'novalidate');

    var formType = form.getAttribute('data-apex-form');
    var alertEl = form.querySelector('[data-apex-form-alert]');
    var successEl = form.querySelector('[data-apex-form-success]');
    var submitBtn = form.querySelector('[type="submit"]');

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      if (!window.ApexFormApi || typeof window.ApexFormApi.submitForm !== 'function') {
        if (alertEl) alertEl.hidden = false;
        return;
      }

      var data;
      if (formType === 'contact' || formType === 'newsletter') {
        var emailInput = form.querySelector('[name="contact[email]"]');
        var email = emailInput ? (emailInput.value || '').trim() : '';
        var emailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!emailValid) {
          if (alertEl) alertEl.hidden = false;
          if (emailInput) {
            var wrap = emailInput.closest('.field');
            if (wrap) wrap.classList.add('is-invalid');
          }
          return;
        }
        if (emailInput) {
          var emailWrap = emailInput.closest('.field');
          if (emailWrap) emailWrap.classList.remove('is-invalid');
        }

        var missingRequired = false;
        Array.prototype.forEach.call(form.querySelectorAll('[required]'), function (el) {
          var wrapper = el.closest('.field');
          var empty =
            el.type === 'checkbox' || el.type === 'radio'
              ? (el.type === 'checkbox' ? !el.checked : !form.querySelector('[name="' + el.name + '"]:checked'))
              : !(el.value || '').trim();
          if (empty) {
            missingRequired = true;
            if (wrapper) wrapper.classList.add('is-invalid');
          } else if (wrapper) {
            wrapper.classList.remove('is-invalid');
          }
        });
        if (missingRequired) {
          if (alertEl) alertEl.hidden = false;
          return;
        }

        data = collectContactFormData(form);
      } else {
        return;
      }

      if (alertEl) alertEl.hidden = true;
      if (successEl) successEl.hidden = true;
      if (submitBtn) submitBtn.disabled = true;

      window.ApexFormApi.submitForm({
        form: formType,
        page: window.location.pathname,
        source: 'shopify-theme',
        data: data
      })
        .then(function () {
          form.reset();
          if (form.getAttribute('data-apex-form-layout') === 'dealer') {
            var root = form.closest('.dealer-app') || form.parentElement;
            var formView = root ? root.querySelector('[data-apex-form-form-view]') : null;
            var successView = root ? root.querySelector('[data-apex-form-success-view]') : null;
            if (formView) formView.hidden = true;
            if (successView) {
              successView.hidden = false;
              if (typeof successView.scrollIntoView === 'function') {
                successView.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }
          } else if (successEl) {
            successEl.hidden = false;
            if (typeof successEl.scrollIntoView === 'function') {
              successEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        })
        .catch(function () {
          if (alertEl) alertEl.hidden = false;
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    });
  }

  function initApexForms() {
    document.querySelectorAll('[data-apex-form]').forEach(bindApexForm);
  }

  window.ApexFormApi = {
    FORM_KEYS: FORM_KEYS,
    normalizeFormKey: normalizeFormKey,

    submitForm: function (payload) {
      var body = Object.assign({}, payload, {
        form: normalizeFormKey(payload.form)
      });
      return apiFetch('/api/forms/submit', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    auth: function (email, password, remember) {
      return apiFetch('/api/auth', {
        method: 'POST',
        body: JSON.stringify({ email: (email || '').trim().toLowerCase(), password: password || '' })
      }).then(function (data) {
        if (data.token) saveAdminToken(data.token, data.expiresIn, remember);
        return data;
      });
    },

    listSubmissions: function (formKey) {
      return apiFetch('/api/admin/submissions/list', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          form: formKey === 'all' ? 'all' : normalizeFormKey(formKey || 'dealer-application')
        })
      });
    },

    listForms: function (formType) {
      return this.listSubmissions(formType).then(function (res) {
        if (res.forms) return res;
        return { ok: true, entries: res.entries || [] };
      });
    },

    updateSubmission: function (formKey, id, patch) {
      var body = { form: normalizeFormKey(formKey), id: id };
      if (typeof patch === 'string') {
        body.status = patch;
      } else if (patch && typeof patch === 'object') {
        if (patch.status) body.status = patch.status;
        if (patch.entry) body.entry = patch.entry;
        if (patch.data) body.data = patch.data;
      }
      return apiFetch('/api/admin/submissions/update', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify(body)
      });
    },

    updateApplication: function (id, status) {
      return this.updateSubmission('dealer-application', id, { status: status });
    },

    provisionDealer: function (id, force) {
      return apiFetch('/api/dealer/provision', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ id: id, force: Boolean(force) })
      });
    },

    retryDealerApplicant: function (id) {
      return apiFetch('/api/dealer/retry-applicant', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ id: id })
      });
    },

    lookupShopifyCustomer: function (payload) {
      return apiFetch('/api/admin/shopify/customer', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          email: payload && payload.email,
          customerId: payload && payload.customerId
        })
      });
    },

    activateDealerPassword: function (payload) {
      return apiFetch('/api/dealer/activate-password', {
        method: 'POST',
        body: JSON.stringify({
          customerId: payload.customerId,
          activationToken: payload.activationToken,
          password: payload.password
        })
      });
    },

    listDealerOrders: function (payload) {
      return apiFetch('/api/dealer/portal/orders', {
        method: 'POST',
        body: JSON.stringify({
          customerId: payload.customerId,
          email: payload.email
        })
      });
    },

    dealerOrderReceipt: function (payload) {
      var url = apiUrl('/api/dealer/portal/orders/receipt');
      return window
        .fetch(url, {
          method: 'POST',
          headers: apiHeaders(),
          body: JSON.stringify({
            customerId: payload.customerId,
            email: payload.email,
            orderId: payload.orderId
          })
        })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () {
              return {};
            }).then(function (data) {
              var err = new Error((data && data.error) || 'Could not load receipt');
              err.status = res.status;
              throw err;
            });
          }
          return res.text();
        });
    },

    deleteSubmission: function (formKey, id) {
      return apiFetch('/api/admin/submissions/delete', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ form: normalizeFormKey(formKey), id: id })
      });
    },

    sendAdminEmail: function (payload) {
      return apiFetch('/api/admin/email/send', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          to: payload && payload.to,
          subject: payload && payload.subject,
          message: payload && (payload.message || payload.text)
        })
      });
    },

    searchAdminOrders: function (payload) {
      return apiFetch('/api/admin/orders/search', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          query: (payload && (payload.query || payload.q || payload.orderName || payload.email)) || ''
        })
      });
    },

    sendOrderTracking: function (payload) {
      return apiFetch('/api/admin/orders/send-tracking', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          orderId: payload && (payload.orderId || payload.order_id),
          trackingNumber: payload && (payload.trackingNumber || payload.tracking_number),
          carrier: payload && (payload.carrier || payload.company),
          trackingUrl: payload && (payload.trackingUrl || payload.tracking_url || payload.url),
          to: payload && (payload.to || payload.email)
        })
      });
    },

    syncNewsletterToBrevo: function () {
      return apiFetch('/api/admin/newsletter/brevo-sync', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({})
      });
    },

    health: function () {
      return apiFetch('/api/health', { method: 'GET' });
    },

    legalTermsUrl: function () {
      var cfg = getConfig();
      return cfg.baseUrl + '/api/legal/terms?t=' + encodeURIComponent(cfg.token);
    },

    fetchLegalTermsPdf: function () {
      var url = this.legalTermsUrl();
      return window
        .fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-store' })
        .then(function (res) {
          if (!res.ok) throw new Error('Could not load terms PDF');
          return res.blob();
        });
    },

    getTermsInfo: function () {
      return apiFetch('/api/admin/legal/terms', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({})
      });
    },

    replaceTermsPdf: function (base64Content) {
      return apiFetch('/api/admin/legal/terms/replace', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ content: base64Content })
      });
    },

    getSubmissionAttachment: function (formKey, id, fileKey) {
      return apiFetch('/api/admin/submissions/file', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({
          form: normalizeFormKey(formKey),
          id: id,
          file: fileKey || 'receipt'
        })
      });
    },

    startPpfCheckout: function (payload) {
      return apiFetch('/api/ppf-plan/checkout', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },

    confirmPpfEnrollment: function (enrollmentId, orderId) {
      var body = { id: enrollmentId };
      if (orderId) body.orderId = orderId;
      return apiFetch('/api/ppf-plan/confirm', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    listActiveEnrollments: function () {
      return apiFetch('/api/admin/active-enrollments/list', {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({})
      });
    },

    clearAdminSession: clearAdminToken,
    readAdminToken: readAdminToken
  };

  function legalTermsPageUrl() {
    var legal = (window.theme && window.theme.apexLegalTerms) || {};
    return (legal.pageUrl || '').trim();
  }

  function initLegalTermsLinks() {
    var pageUrl = legalTermsPageUrl();
    var pdfUrl =
      window.ApexFormApi && typeof window.ApexFormApi.legalTermsUrl === 'function'
        ? window.ApexFormApi.legalTermsUrl()
        : '';

    document.querySelectorAll('[data-apex-terms-link]').forEach(function (el) {
      if (el.tagName !== 'A') return;
      if (pageUrl) el.setAttribute('href', pageUrl);
    });

    document.querySelectorAll('[data-apex-terms-pdf]').forEach(function (el) {
      if (el.tagName !== 'A') return;
      var href = pageUrl || pdfUrl;
      if (!href) return;
      el.setAttribute('href', href);
      if (!pageUrl) {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initApexForms();
      initLegalTermsLinks();
    });
  } else {
    initApexForms();
    initLegalTermsLinks();
  }

  document.addEventListener('shopify:section:load', function () {
    initApexForms();
    initLegalTermsLinks();
  });
})();
